import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { IconButton, Tooltip } from '@mui/material';
import type { CSSProperties, ReactNode } from 'react';

/** What a row needs to be draggable: put `ref` and `style` on the row, and render `handle`. */
export interface SortableRow {
  ref: (el: HTMLElement | null) => void;
  style: CSSProperties;
  handle: ReactNode;
  dragging: boolean;
}

/**
 * A list reordered by dragging each row's handle, with a mouse, a finger
 * (press and hold briefly) or the keyboard (focus the handle, Space, arrows,
 * Space). `onMove` gets the old and new index.
 */
export function SortableList<T extends { id: string }>({
  items,
  disabled = false,
  disabledReason,
  onMove,
  children,
}: {
  items: T[];
  disabled?: boolean;
  /** Shown on the handle when dragging is turned off. */
  disabledReason?: string;
  onMove: (from: number, to: number) => void;
  children: (item: T, index: number, row: SortableRow) => ReactNode;
}) {
  const sensors = useSensors(
    // A small movement first, so clicks on the row still work.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    // Press and hold on touch screens, so the list still scrolls.
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = items.findIndex((i) => i.id === active.id);
    const to = items.findIndex((i) => i.id === over.id);
    if (from >= 0 && to >= 0) onMove(from, to);
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy} disabled={disabled}>
        {items.map((item, index) => (
          <SortableItem key={item.id} id={item.id} disabled={disabled} disabledReason={disabledReason}>
            {(row) => children(item, index, row)}
          </SortableItem>
        ))}
      </SortableContext>
    </DndContext>
  );
}

function SortableItem({
  id,
  disabled,
  disabledReason,
  children,
}: {
  id: string;
  disabled: boolean;
  disabledReason?: string;
  children: (row: SortableRow) => ReactNode;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const handle = (
    <Tooltip title={disabled ? (disabledReason ?? '') : 'Drag to reorder'}>
      <span>
        <IconButton
          ref={setActivatorNodeRef}
          size="small"
          aria-label="Drag to reorder"
          disabled={disabled}
          {...attributes}
          {...listeners}
          sx={{ cursor: disabled ? undefined : isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
  return children({
    ref: setNodeRef,
    style: {
      transform: CSS.Transform.toString(transform),
      transition,
      position: 'relative',
      zIndex: isDragging ? 2 : undefined,
    },
    handle,
    dragging: isDragging,
  });
}
